import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  forkJoin,
  map,
  of,
  tap,
} from 'rxjs';
import { todayDateString } from '../../utils/clinic-date.util';
import { ApiService } from './api.service';
import { AppointmentService } from './appointment.service';
import {
  AppNotification,
  EMPTY_NOTIFICATIONS_SNAPSHOT,
  NotificationsSnapshot,
  getNotificationPolicy,
} from './notification.model';
import { NotificationSoundService } from './notification-sound.service';
import { WorkContextService } from './work-context.service';
import { ToasterService } from '../common/toaster.service';

interface NotificationPrefs {
  readIds: string[];
  dismissedIds: string[];
  heardIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  private readonly pollMs = 15000;
  private readonly snapshotSubject = new BehaviorSubject<NotificationsSnapshot>({
    ...EMPTY_NOTIFICATIONS_SNAPSHOT,
    loading: true,
  });
  readonly snapshot$ = this.snapshotSubject.asObservable();

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private refreshSub?: Subscription;
  private started = false;
  private suppressAlerts = true;
  private readonly apiUrl: string;

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private appointmentService: AppointmentService,
    private workContext: WorkContextService,
    private sound: NotificationSoundService,
    private toaster: ToasterService,
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.refresh();
    this.pollTimer = setInterval(() => this.refresh(true), this.pollMs);
  }

  stop(): void {
    this.started = false;
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.refreshSub?.unsubscribe();
  }

  getSnapshot(): NotificationsSnapshot {
    return this.snapshotSubject.value;
  }

  refresh(quiet = false): void {
    this.refreshSub?.unsubscribe();
    if (!quiet) {
      this.patch({ loading: true, error: null });
    }

    const user = this.getLoggedInUser();
    const role = String(user?.type || '');
    const prefs = this.readPrefs(user?.id);
    const sources: Observable<AppNotification[]>[] = [
      this.loadActivityFeed(user),
    ];

    if (role === 'doctor' || role === 'reception') {
      sources.push(this.loadCrossClinicWaiting(user));
    }

    this.refreshSub = forkJoin(sources)
      .pipe(
        map((groups) => groups.flat()),
        map((items) => this.filterForRole(items, role)),
        map((items) => this.dedupe(items)),
        map((items) => this.applyPrefs(items, prefs)),
        map((items) => this.sortItems(items)),
        tap((items) => this.publish(items, prefs, role)),
        catchError(() => {
          this.patch({
            loading: false,
            error: 'Could not refresh notifications.',
          });
          return of([]);
        }),
      )
      .subscribe();
  }

  markRead(id: string): void {
    const user = this.getLoggedInUser();
    const prefs = this.readPrefs(user?.id);
    if (!prefs.readIds.includes(id)) {
      prefs.readIds.push(id);
      this.writePrefs(user?.id, prefs);
    }
    this.reapplyPrefs();
  }

  markAllRead(): void {
    const user = this.getLoggedInUser();
    const prefs = this.readPrefs(user?.id);
    const ids = this.snapshotSubject.value.items.map((item) => item.id);
    prefs.readIds = Array.from(new Set([...prefs.readIds, ...ids]));
    this.writePrefs(user?.id, prefs);
    this.reapplyPrefs();
  }

  /** Mark read and remove from the inbox. */
  dismiss(id: string): void {
    const user = this.getLoggedInUser();
    const prefs = this.readPrefs(user?.id);
    if (!prefs.readIds.includes(id)) {
      prefs.readIds.push(id);
    }
    if (!prefs.dismissedIds.includes(id)) {
      prefs.dismissedIds.push(id);
    }
    this.writePrefs(user?.id, prefs);
    this.reapplyPrefs();
  }

  private publish(items: AppNotification[], prefs: NotificationPrefs, role: string): void {
    const previousIds = new Set(this.snapshotSubject.value.items.map((item) => item.id));
    const hadItems = previousIds.size > 0;
    const unreadCount = items.filter((item) => !item.read).length;

    this.patch({
      items,
      unreadCount,
      loading: false,
      lastUpdatedAt: new Date().toISOString(),
      error: null,
    });

    // First successful load seeds the inbox quietly. Later polls alert on brand-new ids.
    if (!this.suppressAlerts && hadItems) {
      const fresh = items.filter(
        (item) => !previousIds.has(item.id) && !item.read && !prefs.heardIds.includes(item.id),
      );
      const audible = fresh.find((item) => !!item.sound) || fresh.find((item) => item.priority === 'high');
      if (audible) {
        void this.sound.play(role, String(audible.type));
        prefs.heardIds.push(audible.id);
        // Mark siblings heard so one burst covers a batch.
        fresh.forEach((item) => {
          if (!prefs.heardIds.includes(item.id)) {
            prefs.heardIds.push(item.id);
          }
        });
        this.toaster.info(audible.detail, audible.title);
      }
    }
    this.suppressAlerts = false;

    // Keep recent read/dismissed ids even if an item briefly drops out of the feed,
    // so a poll that reshapes the list does not resurrect unread badges.
    const liveIds = new Set(items.map((item) => item.id));
    const keepRecent = (ids: string[], max = 200) => {
      const keptLive = ids.filter((id) => liveIds.has(id));
      const orphans = ids.filter((id) => !liveIds.has(id)).slice(-40);
      return Array.from(new Set([...keptLive, ...orphans])).slice(-max);
    };
    this.writePrefs(this.getLoggedInUser()?.id, {
      readIds: keepRecent(prefs.readIds),
      dismissedIds: keepRecent(prefs.dismissedIds),
      heardIds: prefs.heardIds.filter((id) => liveIds.has(id)).slice(-120),
    });
  }

  private reapplyPrefs(): void {
    const user = this.getLoggedInUser();
    const prefs = this.readPrefs(user?.id);
    const items = this.applyPrefs(
      this.snapshotSubject.value.items.map((item) => ({ ...item, read: false })),
      prefs,
    );
    this.patch({
      items: this.sortItems(items),
      unreadCount: items.filter((item) => !item.read).length,
    });
  }

  private loadActivityFeed(user: any): Observable<AppNotification[]> {
    const params: Record<string, string> = {
      since: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      limit: '60',
    };
    const clinicId = this.workContext.getClinicId();
    const doctorId =
      user?.type === 'doctor' && user?.id
        ? Number(user.id)
        : this.workContext.getDoctorId();

    // Reception sees clinic-wide activity; doctor (admin) focuses on their consults
    // but still gets self-regs / payments from the unscoped feed fields.
    if (user?.type === 'doctor' && doctorId) {
      params['doctor_id'] = String(doctorId);
    }
    if (clinicId && user?.type !== 'doctor') {
      params['clinic_id'] = String(clinicId);
    }

    return this.http
      .get<any>(`${this.apiUrl}/notifications/feed`, {
        ...this.authHeaders(),
        params,
      })
      .pipe(
        map((res) => {
          const events = Array.isArray(res?.data?.events) ? res.data.events : [];
          return events.map((event: any) => this.mapFeedEvent(event));
        }),
        catchError(() => of([])),
      );
  }

  private loadCrossClinicWaiting(user: any): Observable<AppNotification[]> {
    if (user?.type !== 'doctor' || !user?.id) {
      return of([]);
    }

    return this.appointmentService
      .getDoctorQueueSummary({ doctor_id: user.id, date: todayDateString() })
      .pipe(
        map((res: any) => {
          const clinics = Array.isArray(res?.data?.clinics) ? res.data.clinics : [];
          const currentClinicId = Number(this.workContext.getClinicId() || 0);
          return clinics
            .filter((clinic: any) => {
              const waiting = Number(clinic.waiting || 0);
              const clinicId = Number(clinic.clinic_id || 0);
              return waiting > 0 && currentClinicId > 0 && clinicId !== currentClinicId;
            })
            .map((clinic: any) => {
              const waiting = Number(clinic.waiting || 0);
              return {
                id: `cross-clinic-${clinic.clinic_id}-${todayDateString()}`,
                type: 'cross_clinic_waiting',
                category: 'queue' as const,
                priority: 'high' as const,
                title: `${waiting} waiting at ${clinic.clinic_name || 'another clinic'}`,
                detail: 'Patients are checked in for you at another clinic.',
                path: '/admin/prescriptions',
                createdAt: clinic.latest_checked_in_at || clinic.latest_added_at || null,
                read: false,
                dismissible: true,
                sound: true,
                clinicId: Number(clinic.clinic_id || 0),
                doctorId: Number(user.id),
                meta: { clinic_name: clinic.clinic_name, waiting },
              } satisfies AppNotification;
            });
        }),
        catchError(() => of([])),
      );
  }

  private mapFeedEvent(event: any): AppNotification {
    return {
      id: String(event.id),
      type: String(event.type || 'system'),
      category: (event.category || 'system') as AppNotification['category'],
      priority: (event.priority || 'normal') as AppNotification['priority'],
      title: String(event.title || 'Clinic update'),
      detail: String(event.detail || ''),
      path: event.path || undefined,
      createdAt: event.created_at || null,
      read: false,
      dismissible: event.type !== 'self_registration',
      sound: !!event.sound,
      actorUserId: event.actor_user_id != null ? Number(event.actor_user_id) : null,
      clinicId: event.clinic_id != null ? Number(event.clinic_id) : null,
      doctorId: event.doctor_id != null ? Number(event.doctor_id) : null,
      meta: event.meta || {},
    };
  }

  private filterForRole(items: AppNotification[], role: string): AppNotification[] {
    const userId = Number(this.getLoggedInUser()?.id || 0);
    const filtered: AppNotification[] = [];

    for (const item of items) {
      const policy = getNotificationPolicy(role, String(item.type));
      if (!policy.visible) {
        continue;
      }
      const ownAction = !!(item.actorUserId && userId && item.actorUserId === userId);
      const sound = policy.sound && !(role === 'reception' && ownAction);
      filtered.push({
        ...item,
        sound,
        path: this.pathForRole(role, item),
      });
    }

    return filtered;
  }

  private pathForRole(role: string, item: AppNotification): string | undefined {
    if (item.type === 'medicine_queue') {
      if (role === 'pharmacy') {
        return '/admin/pharmacy';
      }
      if (role === 'doctor') {
        return '/admin/prescriptions';
      }
      return '/admin/appointment-list';
    }
    if (item.type === 'patient_checked_in' || item.type === 'cross_clinic_waiting') {
      return role === 'doctor' ? '/admin/prescriptions' : '/admin/appointment';
    }
    if (item.type === 'payment_received' || item.type === 'appointment_completed') {
      return role === 'doctor' ? '/admin/prescriptions' : (item.path || '/admin/appointment-list');
    }
    return item.path;
  }

  private dedupe(items: AppNotification[]): AppNotification[] {
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }

  private applyPrefs(items: AppNotification[], prefs: NotificationPrefs): AppNotification[] {
    const dismissed = new Set(prefs.dismissedIds);
    const read = new Set(prefs.readIds);
    return items
      .filter((item) => !dismissed.has(item.id))
      .map((item) => ({
        ...item,
        read: read.has(item.id),
      }));
  }

  private sortItems(items: AppNotification[]): AppNotification[] {
    const priorityRank = { high: 0, normal: 1, low: 2 };
    return [...items].sort((a, b) => {
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      const p = priorityRank[a.priority] - priorityRank[b.priority];
      if (p !== 0) {
        return p;
      }
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
  }

  private getLoggedInUser(): any {
    try {
      return JSON.parse(sessionStorage.getItem('loggedInUser') || '{}');
    } catch {
      return {};
    }
  }

  private authHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
      }),
    };
  }

  private prefsKey(userId: unknown): string {
    const id = userId != null ? String(userId) : 'anon';
    return `clinic_notifications_v2_${id}_${todayDateString()}`;
  }

  private readPrefs(userId: unknown): NotificationPrefs {
    try {
      const raw = localStorage.getItem(this.prefsKey(userId));
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        readIds: Array.isArray(parsed?.readIds) ? parsed.readIds.map(String) : [],
        dismissedIds: Array.isArray(parsed?.dismissedIds) ? parsed.dismissedIds.map(String) : [],
        heardIds: Array.isArray(parsed?.heardIds) ? parsed.heardIds.map(String) : [],
      };
    } catch {
      return { readIds: [], dismissedIds: [], heardIds: [] };
    }
  }

  private writePrefs(userId: unknown, prefs: NotificationPrefs): void {
    localStorage.setItem(this.prefsKey(userId), JSON.stringify(prefs));
  }

  private patch(partial: Partial<NotificationsSnapshot>): void {
    this.snapshotSubject.next({
      ...this.snapshotSubject.value,
      ...partial,
    });
  }
}
